import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Input,
    Textarea,
    Heading,
    Spinner,
    FormControl,
    FormLabel,
    Text,
    useToast
} from "@chakra-ui/react";
import { useAuth } from "../../contexts/AuthContext";
import { SupabaseClient } from "../../supabaseClient";
import i18n, {t} from "i18next";
import MobilePhotoPicker from "../../components/MobilePhotoPIcker";

/**
 * MealEntryForm – with photo‑to‑text support but **without** storage.
 */
export default function MealEntryForm({ initialEntry = null, onSuccess }) {
    const isEditMode = Boolean(initialEntry);

    /* ---------- local state ---------- */
    const [foodDescription, setFoodDescription] = useState(
        initialEntry?.food_description || ""
    );
    const [mealDate, setMealDate] = useState(
        initialEntry?.meal_date || new Date().toISOString().split("T")[0]
    );
    const [note, setNote] = useState(initialEntry?.note || "");

    const [photoFile, setPhotoFile] = useState(null);
    const [isAnalysingPhoto, setIsAnalyzingPhoto] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const { user } = useAuth();
    const toast = useToast();

    /* ---------- sync when editing ---------- */
    useEffect(() => {
        if (initialEntry) {
            setFoodDescription(initialEntry.food_description);
            setMealDate(initialEntry.meal_date);
            setNote(initialEntry.note || "");
        }
    }, [initialEntry]);

    // whenever a new photo is set
    useEffect(() => {
        if (!photoFile) return;

        const describe = async () => {
            setIsAnalyzingPhoto(true);
            try {
                const form = new FormData();
                form.append("image", photoFile);
                form.append("language", i18n.language);

                const res = await fetch("/api/describeMealPhoto", {
                    method: "POST", body: form
                });
                if (!res.ok) throw new Error("Failed to describe image");
                const { description } = await res.json();        // ← your API returns this

                // merge with anything the user has already typed
                setFoodDescription(prev => prev.trim() ? `${prev}\n${description}` : description);
            } catch (err) {
                toast({
                    title: "Image analysis failed",
                    description: err.message,
                    status: "error",
                    duration: 3000,
                    isClosable: true
                });
            } finally {
                setIsAnalyzingPhoto(false);
            }
        };

        describe();
    }, [photoFile, toast]);

    /* ---------- save ---------- */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!foodDescription.trim()) {
            toast({
                title: t('mealForm.missingInfoTitle', 'Missing information'),
                description: t(
                    'mealForm.missingInfoBody',
                    'Add a photo or type a food description.'
                ),
                status: "error",
                duration: 3000,
                isClosable: true
            });
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                food_description: foodDescription,
                meal_date: mealDate,
                note: note.trim() || null
            };

            const table = SupabaseClient.from("meal_logs");
            const result = isEditMode
                ? await table
                    .update(payload)
                    .eq("id", initialEntry.id)
                    .select("*")
                    .single()
                : await table
                    .insert([{ ...payload, profile_id: user.id }])
                    .select("*")
                    .single();

            if (result.error) throw result.error;

            // kick off nutrition processing –·no photoUrl now
            fetch("/api/processMeal", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    mealLogId: result.data.id,
                    foodDescription,
                    language: i18n.language
                })
            }).catch((err) => console.error("processMeal failed:", err));

            toast({
                title: isEditMode ? "Meal updated!" : "Meal saved!",
                status: "success",
                duration: 2500,
                isClosable: true
            });

            onSuccess?.(result.data);
            if (!isEditMode) {
                setFoodDescription("");
                setMealDate(new Date().toISOString().split("T")[0]);
                setNote("");
            }
        } catch (err) {
            toast({
                title: "Error saving meal",
                description: err.message,
                status: "error",
                duration: 4000,
                isClosable: true
            });
        } finally {
            setIsSaving(false);
        }
    };

    // ---------- UI ----------
    if (user === undefined) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" h="200px">
                <Spinner size="lg" />
            </Box>
        );
    }

    return (
        <Box maxW="500px" mx="auto" p={4}>
            <Heading as="h2" size="lg" textAlign="center" mb={4}>
                {isEditMode ? "Edit Meal Entry" : "Meal Entry"}
            </Heading>

            <form onSubmit={handleSubmit}>
                {/* photo input – not saved, just analysed */}
                <FormControl mb={4}>
                    <FormLabel>{t("mealForm.photo")}</FormLabel>
                    <MobilePhotoPicker onFile={setPhotoFile} />
                    {photoFile && (
                        <Text fontSize="xs" mt={1} color="gray.500">
                            {t('mealForm.photoDisclaimer')}
                        </Text>
                    )}
                    {isAnalysingPhoto && (
                        <Text fontSize="sm" mt={2} color="gray.500">
                            {t("mealForm.pending")}
                        </Text>
                    )}
                </FormControl>

                <FormControl mb={4}>
                    <FormLabel>{t('mealForm.description')}</FormLabel>
                    <Textarea
                        value={foodDescription}
                        onChange={(e) => setFoodDescription(e.target.value)}
                        isDisabled={isAnalysingPhoto}
                    />
                </FormControl>

                <FormControl mb={4}>
                    <FormLabel>{t('mealForm.date')}</FormLabel>
                    <Input
                        type="date"
                        value={mealDate}
                        onChange={(e) => setMealDate(e.target.value)}
                        max={new Date().toISOString().split("T")[0]}
                    />
                </FormControl>

                <FormControl mb={4}>
                    <FormLabel>{t('mealForm.note')}</FormLabel>
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} />
                </FormControl>

                <Button
                    type="submit"
                    colorScheme="blue"
                    isLoading={isSaving}
                    isDisabled={isAnalysingPhoto || !foodDescription.trim()}
                    width="full"
                >
                    {isEditMode ? "Update Meal" : "Save Meal"}
                </Button>
            </form>
        </Box>
    );
}